import { useNavigate } from 'react-router-dom'

export function Terms() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white">
      <nav
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid #f3f4f6',
          position: 'sticky',
          top: 0,
          zIndex: 50,
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1rem' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 0',
            }}
          >
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <img
                src="/favicon.png"
                alt="Little Sister Logo"
                style={{ height: '32px', width: '32px', objectFit: 'contain' }}
              />
              <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e3a5f' }}>
                Little Sister
              </span>
            </div>
          </div>
        </div>
      </nav>

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '3rem 1rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#1e3a5f', marginBottom: '0.5rem' }}>
          Terms and Conditions
        </h1>
        <p style={{ color: '#6b7280', marginBottom: '0.25rem' }}>
          <strong>Little Sister — Medication Reminder Service</strong>
        </p>
        <p style={{ color: '#6b7280', marginBottom: '0.25rem' }}>
          <strong>Last Updated:</strong> March 27, 2026
        </p>
        <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
          <strong>Effective Date:</strong> March 27, 2026
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <Section title="1. Agreement to Terms">
            <p>
              By creating an account, using the Little Sister application, or receiving calls from
              our Service, you ("User," "you," or "your") agree to be bound by these Terms and
              Conditions ("Terms"). If you do not agree, you should not sign up or use the Service.
            </p>
            <p>These Terms apply to all users, including:</p>
            <ul style={listStyle}>
              <li>
                <strong>Caregivers</strong> — individuals who create accounts and manage medication
                schedules for loved ones
              </li>
              <li>
                <strong>Loved Ones</strong> — individuals who receive medication reminder calls from
                the Service
              </li>
              <li>
                <strong>Secondary Caregivers</strong> — individuals invited by a primary caregiver
                to view or manage a loved one's information
              </li>
            </ul>
          </Section>

          <Section title="2. Description of Service">
            <p>Little Sister is an AI-powered medication reminder service that:</p>
            <ol style={{ ...listStyle, listStyleType: 'decimal' }}>
              <li>Allows caregivers to set up medication schedules for elderly loved ones</li>
              <li>
                Places automated phone calls to loved ones at scheduled times to remind them to take
                their medications
              </li>
              <li>
                Reports call outcomes (medication taken, missed, or declined) to designated
                caregivers via SMS and/or email
              </li>
              <li>
                Provides a web-based dashboard for caregivers to view medication schedules, call
                history, and adherence patterns
              </li>
              <li>Supports voice-based onboarding for account setup via phone call</li>
            </ol>
          </Section>

          <Section title="3. Medical Disclaimer">
            <p>
              <strong>IMPORTANT: PLEASE READ THIS SECTION CAREFULLY.</strong>
            </p>

            <h4 style={subheadingStyle}>3.1 Not a Medical Service</h4>
            <p>
              Little Sister is a <strong>convenience and communication tool only</strong>. We are:
            </p>
            <ul style={listStyle}>
              <li>
                <strong>NOT</strong> a healthcare provider, pharmacy, medical device, or clinical
                service
              </li>
              <li>
                <strong>NOT</strong> a substitute for professional medical advice, diagnosis, or
                treatment
              </li>
              <li>
                <strong>NOT</strong> responsible for verifying the accuracy, safety, or
                appropriateness of any medication information entered by users
              </li>
            </ul>

            <h4 style={subheadingStyle}>3.2 No Medical Advice</h4>
            <p>The Service does not:</p>
            <ul style={listStyle}>
              <li>Prescribe, recommend, or advise on medications</li>
              <li>
                Verify medications against prescriptions, medical records, or drug interaction
                databases
              </li>
              <li>Provide dosage guidance or medical opinions</li>
              <li>Monitor vital signs or health conditions</li>
              <li>Replace any part of a healthcare professional's care</li>
            </ul>

            <h4 style={subheadingStyle}>3.3 User Responsibility for Medical Decisions</h4>
            <ul style={listStyle}>
              <li>
                All medication information entered into the Service is provided by the caregiver and
                is the caregiver's sole responsibility
              </li>
              <li>
                You must consult qualified healthcare professionals for all medical decisions,
                including medication changes
              </li>
              <li>
                You should not rely on the Service as your only method of medication management
              </li>
              <li>
                Always follow the instructions of your physician, pharmacist, or other licensed
                healthcare provider
              </li>
            </ul>

            <h4 style={subheadingStyle}>3.4 No Guarantee of Health Outcomes</h4>
            <p>
              Little Sister makes <strong>no guarantees</strong> regarding health outcomes. We are
              not liable for any adverse health effects, including but not limited to:
            </p>
            <ul style={listStyle}>
              <li>Missed or incorrect medication doses</li>
              <li>Drug interactions or side effects</li>
              <li>Health complications arising from medication adherence or non-adherence</li>
              <li>Any injury, illness, or death related to medication use</li>
            </ul>
          </Section>

          <Section title="4. AI Disclosure and Limitations">
            <h4 style={subheadingStyle}>4.1 AI-Powered Calls</h4>
            <p>
              All phone calls made by Little Sister are conducted by an{' '}
              <strong>artificial intelligence assistant</strong>. You are <strong>not</strong>{' '}
              speaking with a human when you receive a call from Little Sister.
            </p>

            <h4 style={subheadingStyle}>4.2 AI Limitations</h4>
            <p>The AI assistant:</p>
            <ul style={listStyle}>
              <li>
                May misunderstand spoken responses, particularly in noisy environments, with strong
                accents, or in languages it supports imperfectly
              </li>
              <li>May produce errors in speech-to-text transcription</li>
              <li>May generate inaccurate call summaries</li>
              <li>Cannot make medical judgments or provide emergency assistance</li>
              <li>Cannot detect emergencies, falls, or medical distress</li>
              <li>
                May experience technical issues that prevent calls from being placed or completed
              </li>
            </ul>

            <h4 style={subheadingStyle}>4.3 Transcript Accuracy</h4>
            <p>
              Call transcripts are generated automatically using speech-to-text technology and may
              contain errors. Transcripts should not be relied upon as verbatim records of
              conversations.
            </p>
          </Section>

          <Section title="5. User Accounts and Responsibilities">
            <h4 style={subheadingStyle}>5.1 Account Creation</h4>
            <ul style={listStyle}>
              <li>You must be at least 18 years old to use the Service or create an account</li>
              <li>You must provide accurate and complete information during registration</li>
              <li>You are responsible for maintaining the security of your account credentials</li>
              <li>
                You must have the legal authority to manage medications for the loved one you
                register (e.g., as a family member, legal guardian, or authorized representative)
              </li>
            </ul>

            <h4 style={subheadingStyle}>5.2 Caregiver Responsibilities</h4>
            <p>As a caregiver, you are responsible for:</p>
            <ul style={listStyle}>
              <li>Entering accurate medication names, dosages, schedules, and notes</li>
              <li>Keeping medication schedules up to date when prescriptions change</li>
              <li>
                Obtaining appropriate authorization from the loved one (or their legal
                representative) before registering them for reminder calls
              </li>
              <li>Monitoring call outcomes and following up on missed medications</li>
              <li>
                Informing the loved one that they will receive automated AI calls from Little Sister
              </li>
              <li>Using the vacation pause feature when calls should be temporarily stopped</li>
            </ul>

            <h4 style={subheadingStyle}>5.3 Loved One Participation</h4>
            <ul style={listStyle}>
              <li>
                Loved ones will be asked for verbal consent during an introductory call before
                regular reminders begin
              </li>
              <li>
                Loved ones may decline consent at any time, and no further calls will be placed
              </li>
              <li>Loved ones should inform their caregiver if they wish to stop receiving calls</li>
            </ul>

            <h4 style={subheadingStyle}>5.4 Secondary Caregivers</h4>
            <ul style={listStyle}>
              <li>Secondary caregivers are invited by a primary caregiver via email invitation</li>
              <li>Invitations expire after 7 days</li>
              <li>
                Secondary caregivers receive view-only or full access as determined by the primary
                caregiver
              </li>
              <li>
                The primary caregiver remains responsible for the accuracy of medication information
              </li>
            </ul>
          </Section>

          <Section title="6. Consent for Automated Communications">
            <h4 style={subheadingStyle}>6.1 Phone Calls to Loved Ones</h4>
            <p>
              By registering a loved one's phone number and setting up medication reminders, you
              consent on their behalf to Little Sister placing automated, AI-generated phone calls
              to that number. You represent that you have the authority to provide this consent or
              that the loved one has been informed and agrees to receive such calls.
            </p>
            <p>
              Verbal consent is additionally collected directly from the loved one during the first
              reminder call. If the loved one declines, no further calls will be made.
            </p>

            <h4 style={subheadingStyle}>
              6.2 SMS Notifications to Caregivers (Little Sister Alerts)
            </h4>
            <p>
              By enabling SMS notifications and providing your phone number, you consent to receive
              automated text messages from the Little Sister Alerts program regarding:
            </p>
            <ul style={listStyle}>
              <li>Medication reminder outcomes (taken, missed, declined)</li>
              <li>System notifications related to the Service</li>
            </ul>
            <p>
              <strong>Message frequency:</strong> You will receive approximately 1–5 SMS messages
              per day, depending on how many medication reminders are scheduled for your loved one.
              Message frequency varies based on your medication schedule configuration.
            </p>
            <p>
              <strong>Message and data rates may apply.</strong> Please contact your wireless
              carrier for details about your messaging plan.
            </p>
            <p>
              <strong>To get help,</strong> text <strong>HELP</strong> to our messaging number or
              contact us at{' '}
              <a href="mailto:wing@littlesister.ai" style={linkStyle}>
                wing@littlesister.ai
              </a>
              .
            </p>
            <p>
              <strong>To opt out,</strong> text <strong>STOP</strong> to our messaging number at any
              time. You will receive a confirmation message and no further SMS messages will be
              sent. You may also withdraw SMS consent through your account settings.
            </p>

            <h4 style={subheadingStyle}>6.3 Email Notifications to Caregivers</h4>
            <p>
              By enabling email notifications and providing your email address, you consent to
              receive automated emails regarding:
            </p>
            <ul style={listStyle}>
              <li>Medication reminder outcomes</li>
              <li>Account-related notifications (invitations, account changes)</li>
            </ul>
            <p>You may withdraw email consent at any time through your account settings.</p>

            <h4 style={subheadingStyle}>6.4 Compliance with Telecommunications Laws</h4>
            <p>
              Little Sister is designed to comply with applicable telecommunications regulations,
              including the Telephone Consumer Protection Act (TCPA) in the United States. By using
              the Service:
            </p>
            <ul style={listStyle}>
              <li>You acknowledge that automated calls will be placed using an artificial voice</li>
              <li>You consent to such calls being made to the phone numbers you provide</li>
              <li>You understand that message and data rates may apply for SMS notifications</li>
              <li>You may opt out of automated communications at any time</li>
            </ul>
          </Section>

          <Section title="7. Acceptable Use">
            <p>
              You agree <strong>not</strong> to:
            </p>
            <ul style={listStyle}>
              <li>
                Provide false or misleading information about medications, loved ones, or your
                identity
              </li>
              <li>
                Use the Service for any person without their knowledge or appropriate legal
                authority
              </li>
              <li>Attempt to access other users' accounts or data</li>
              <li>Interfere with or disrupt the Service, its servers, or connected networks</li>
              <li>Use the Service for any unlawful purpose</li>
              <li>
                Reverse engineer, decompile, or attempt to extract the source code of the Service
              </li>
              <li>Use the Service to harass, abuse, or harm any person</li>
              <li>
                Resell, redistribute, or commercialize the Service without our written permission
              </li>
            </ul>
          </Section>

          <Section title="8. Service Availability and Limitations">
            <h4 style={subheadingStyle}>8.1 No Guarantee of Availability</h4>
            <p>
              We strive to provide reliable service, but we do <strong>not</strong> guarantee that:
            </p>
            <ul style={listStyle}>
              <li>The Service will be available 24/7 without interruption</li>
              <li>All scheduled calls will be successfully placed and completed</li>
              <li>Phone connections will be clear and error-free</li>
              <li>The AI assistant will correctly understand every response</li>
              <li>SMS or email notifications will be delivered without delay</li>
            </ul>

            <h4 style={subheadingStyle}>8.2 Retry Policy</h4>
            <p>
              If a scheduled reminder call is not answered, the Service will attempt up to{' '}
              <strong>3 total call attempts</strong>. After all attempts are exhausted, the
              caregiver will be notified that the call was missed.
            </p>

            <h4 style={subheadingStyle}>8.3 Technical Requirements</h4>
            <ul style={listStyle}>
              <li>Loved ones need a working phone number capable of receiving voice calls</li>
              <li>
                Caregivers need a modern web browser and internet connection to access the dashboard
              </li>
              <li>SMS notifications require a phone capable of receiving text messages</li>
            </ul>

            <h4 style={subheadingStyle}>8.4 Maintenance and Downtime</h4>
            <p>
              We may temporarily suspend the Service for maintenance, updates, or security reasons.
              We will make reasonable efforts to schedule maintenance during off-peak hours and
              provide advance notice when possible.
            </p>
          </Section>

          <Section title="9. Intellectual Property">
            <h4 style={subheadingStyle}>9.1 Our Property</h4>
            <p>
              The Service, including its software, design, AI models, branding, and documentation,
              is owned by Little Sister and protected by intellectual property laws. You may not
              copy, modify, distribute, or create derivative works from the Service.
            </p>

            <h4 style={subheadingStyle}>9.2 Your Content</h4>
            <p>
              You retain ownership of the information you provide to the Service (medication
              details, personal information, etc.). By using the Service, you grant us a limited
              license to use this information solely for the purpose of providing the Service to
              you.
            </p>
          </Section>

          <Section title="10. Limitation of Liability">
            <p>
              <strong>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:</strong>
            </p>

            <h4 style={subheadingStyle}>10.1 Disclaimer of Warranties</h4>
            <p style={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.9rem' }}>
              The Service is provided "as is" and "as available" without warranties of any kind,
              either express or implied, including but not limited to implied warranties of
              merchantability, fitness for a particular purpose, or non-infringement.
            </p>

            <h4 style={subheadingStyle}>10.2 Limitation of Damages</h4>
            <p>
              <strong>
                In no event shall Little Sister, its officers, directors, employees, or agents be
                liable for:
              </strong>
            </p>
            <ul style={listStyle}>
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Any loss of profits, data, or goodwill</li>
              <li>Any damages arising from your use of or inability to use the Service</li>
              <li>
                Any damages arising from missed medication reminders, whether due to technical
                failure, AI error, or any other cause
              </li>
              <li>
                Any damages related to health outcomes, medication adherence, or medical decisions
              </li>
              <li>
                Any damages exceeding the total amount you paid to Little Sister in the twelve (12)
                months preceding the claim
              </li>
            </ul>

            <h4 style={subheadingStyle}>10.3 Essential Purpose</h4>
            <p>
              Some jurisdictions do not allow the exclusion or limitation of certain warranties or
              damages. In such jurisdictions, our liability shall be limited to the greatest extent
              permitted by law.
            </p>
          </Section>

          <Section title="11. Indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless Little Sister and its officers,
              directors, employees, and agents from any claims, damages, losses, or expenses
              (including reasonable attorney fees) arising from:
            </p>
            <ul style={listStyle}>
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your provision of inaccurate medication or personal information</li>
              <li>Your registration of a loved one without proper authority</li>
              <li>Any claim by a third party related to your use of the Service</li>
            </ul>
          </Section>

          <Section title="12. Account Termination">
            <h4 style={subheadingStyle}>12.1 Termination by You</h4>
            <p>
              You may request account deletion at any time by contacting us at{' '}
              <a href="mailto:wing@littlesister.ai" style={linkStyle}>
                wing@littlesister.ai
              </a>
              . Upon deletion:
            </p>
            <ul style={listStyle}>
              <li>
                All your personal data, medication records, call transcripts, and associated records
                are permanently deleted
              </li>
              <li>Active medication reminders and scheduled calls are cancelled immediately</li>
              <li>This action is irreversible</li>
            </ul>

            <h4 style={subheadingStyle}>12.2 Termination by Us</h4>
            <p>We may suspend or terminate your account if:</p>
            <ul style={listStyle}>
              <li>You violate these Terms</li>
              <li>You provide false or misleading information</li>
              <li>We are required to do so by law</li>
              <li>We discontinue the Service</li>
            </ul>
            <p>
              We will make reasonable efforts to notify you before termination, except where
              prohibited by law or where immediate termination is necessary to protect the Service
              or other users.
            </p>

            <h4 style={subheadingStyle}>12.3 Effect of Termination</h4>
            <p>Upon termination:</p>
            <ul style={listStyle}>
              <li>Your right to use the Service ceases immediately</li>
              <li>Scheduled calls will no longer be placed</li>
              <li>Caregiver notifications will stop</li>
              <li>Data retention is governed by our Privacy Policy</li>
            </ul>
          </Section>

          <Section title="13. Dispute Resolution">
            <h4 style={subheadingStyle}>13.1 Governing Law</h4>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the
              State of Delaware, without regard to its conflict of law provisions.
            </p>

            <h4 style={subheadingStyle}>13.2 Informal Resolution</h4>
            <p>
              Before filing any formal legal claim, you agree to first contact us at{' '}
              <a href="mailto:wing@littlesister.ai" style={linkStyle}>
                wing@littlesister.ai
              </a>{' '}
              and attempt to resolve the dispute informally for at least 30 days.
            </p>
          </Section>

          <Section title="14. Secondary Caregiver Invitations">
            <ul style={listStyle}>
              <li>Primary caregivers may invite secondary caregivers via email</li>
              <li>Invitations contain a secure token and expire after 7 days</li>
              <li>By accepting an invitation, the secondary caregiver agrees to these Terms</li>
              <li>
                The primary caregiver is responsible for managing secondary caregiver access and can
                revoke it at any time
              </li>
            </ul>
          </Section>

          <Section title="15. Language and Geographic Scope">
            <p>
              The Service is operated in and intended for users located in the United States. It is
              available in English and Traditional Chinese (繁體中文) to serve the linguistic needs
              of our US-based users. In the event of any conflict between translated versions of
              these Terms, the English version shall prevail.
            </p>
          </Section>

          <Section title="16. Changes to These Terms">
            <p>We may modify these Terms at any time. When we make material changes, we will:</p>
            <ul style={listStyle}>
              <li>Update the "Last Updated" date at the top of these Terms</li>
              <li>Notify caregivers via their preferred notification method</li>
              <li>Post the updated Terms on our website</li>
            </ul>
            <p>
              Your continued use of the Service after changes take effect constitutes acceptance of
              the revised Terms. If you do not agree with the changes, you must stop using the
              Service and request account deletion.
            </p>
          </Section>

          <Section title="17. Severability">
            <p>
              If any provision of these Terms is found to be invalid or unenforceable, the remaining
              provisions shall remain in full force and effect. The invalid or unenforceable
              provision shall be modified to the minimum extent necessary to make it valid and
              enforceable.
            </p>
          </Section>

          <Section title="18. Entire Agreement">
            <p>
              These Terms, together with the{' '}
              <a href="/privacy" style={linkStyle}>
                Privacy Policy
              </a>
              , constitute the entire agreement between you and Little Sister regarding the Service
              and supersede all prior agreements and understandings.
            </p>
          </Section>

          <Section title="19. Contact Us">
            <p>
              If you have questions about these Terms, please contact us at{' '}
              <a href="mailto:wing@littlesister.ai" style={linkStyle}>
                wing@littlesister.ai
              </a>
              .
            </p>
          </Section>

          <p
            style={{
              marginTop: '1rem',
              padding: '1.5rem',
              backgroundColor: '#f9fafb',
              borderRadius: '0.5rem',
              fontWeight: 600,
              color: '#374151',
              textAlign: 'center',
            }}
          >
            By using Little Sister, you acknowledge that you have read, understood, and agree to be
            bound by these Terms and Conditions and our{' '}
            <a href="/privacy" style={linkStyle}>
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2
        style={{
          fontSize: '1.375rem',
          fontWeight: 600,
          color: '#1e3a5f',
          marginBottom: '1rem',
          paddingBottom: '0.5rem',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        {title}
      </h2>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          color: '#374151',
          lineHeight: 1.7,
        }}
      >
        {children}
      </div>
    </section>
  )
}

const subheadingStyle: React.CSSProperties = {
  fontSize: '1.1rem',
  fontWeight: 600,
  color: '#1e3a5f',
  marginTop: '0.5rem',
}

const listStyle: React.CSSProperties = {
  paddingLeft: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
}

const linkStyle: React.CSSProperties = {
  color: '#3b82f6',
  textDecoration: 'underline',
}
